import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { RangeValue } from 'rc-picker/es/interface';
import React, { useCallback, useMemo } from 'react';

import { RangePicker } from 'app/components/fields/RangePicker/RangePicker';
import { TextArea } from 'app/components/fields/TextArea/TextArea';
import { dayjs } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';

import './DisclaimerBloc.scss';

type OnChangeArg = {
  id: number | null;
  text: string;
  startDate: string | null;
  endDate: string | null;
  temporaryId: number;
  priority: number;
};

type IProps = {
  dndId: string;
  mode: 'view' | 'edit';
  text: string;
  startDate: string | null;
  endDate: string | null;
  id: number | null;
  priority: number;
  temporaryId: number;
  onChange: (value: OnChangeArg) => void;
  onRemove: () => void;
};

export const DisclaimerBloc = ({
  dndId,
  mode,
  text,
  startDate,
  endDate,
  id,
  temporaryId,
  priority,
  onChange,
  onRemove,
}: IProps) => {
  const editing = mode === 'edit';

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dndId, disabled: !editing });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const RangePickerValue = useMemo<RangeValue<Dayjs | null>>(() => {
    return [
      startDate ? dayjs(startDate, 'YYYY-MM-DD') : null,
      endDate ? dayjs(endDate, 'YYYY-MM-DD') : null,
    ];
  }, [startDate, endDate]);

  const onTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange({
        text: event.target.value,
        startDate,
        endDate,
        id,
        temporaryId,
        priority,
      });
    },
    [onChange, id, priority, startDate, endDate, temporaryId]
  );

  const onDateChange = useCallback(
    (rangeValue: RangeValue<Dayjs | null>) => {
      onChange({
        text,
        startDate: (rangeValue && rangeValue[0]?.format('YYYY-MM-DD')) || null,
        endDate: (rangeValue && rangeValue[1]?.format('YYYY-MM-DD')) || null,
        id,
        priority,
        temporaryId,
      });
    },
    [text, id, priority, temporaryId, onChange]
  );

  return (
    <div ref={setNodeRef} style={style} className="disclaimer-bloc">
      <div className="disclaimer-left">
        <TextArea
          editing={editing}
          value={text}
          label="Message"
          id={`${temporaryId}-textarea`}
          onChange={onTextChange}
          rows={3}
        />
        <div className="spacer" />
        <RangePicker
          editing={editing}
          value={RangePickerValue}
          label="Date range"
          id={`${temporaryId}-rangepicker`}
          onChange={onDateChange}
        />
      </div>
      {editing && (
        <div className="disclaimer-right">
          <DeleteOutlined onClick={onRemove} />
          <HolderOutlined
            className="disclaimer-drag-handle"
            {...attributes}
            {...listeners}
          />
        </div>
      )}
    </div>
  );
};
